import React, { useState, useEffect, useRef, useCallback } from "react";
import ReactDOM from "react-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronLeft,
  faChevronRight,
  faAnglesLeft,
  faAnglesRight,
  faClock,
  faCalendar,
} from "@awesome.me/kit-70c1f0055f/icons/classic/regular";

import { AnimatePresence, motion } from "framer-motion";
import clsx from "clsx";
import FormInput from "../form-input";
import {
  completeRangeWithEnd,
  getEndDatePresetLabel,
  isEndDatePreset,
  isPickingEndDate,
} from "./datePickerRangeUtils";

// Utility functions
const daysOfWeek = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

function classNames(...classes: (string | boolean | undefined)[]) {
  return classes.filter(Boolean).join(" ");
}

function getDaysInMonth(year: number, month: number) {
  return new Date(year, month + 1, 0).getDate();
}

function getFirstDayOfMonth(year: number, month: number) {
  return new Date(year, month, 1).getDay();
}

// Helper function to format date as YYYY-MM-DD
const formatDate = (date: Date): string => {
  return date.toISOString().split("T")[0];
};

// Helper function to format time as HH:MM
const formatTime = (date: Date): string => {
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${hours}:${minutes}`;
};

// Helper function to convert YYYY-MM-DD to DD/MM/YYYY
export const formatDateForDisplay = (
  dateStr: string,
  format: "YYYY-MM-DD" | "DD/MM/YYYY" = "DD/MM/YYYY"
): string => {
  if (!dateStr || format === "YYYY-MM-DD") return dateStr;

  // Convert YYYY-MM-DD to DD/MM/YYYY
  const parts = dateStr.split("-");
  if (parts.length === 3) {
    return `${parts[2]}/${parts[1]}/${parts[0]}`;
  }
  return dateStr;
};

// Helper function to convert DD/MM/YYYY to YYYY-MM-DD
export const parseDisplayDate = (
  displayStr: string,
  format: "YYYY-MM-DD" | "DD/MM/YYYY" = "DD/MM/YYYY"
): string => {
  if (!displayStr || format === "YYYY-MM-DD") return displayStr;

  // Convert DD/MM/YYYY to YYYY-MM-DD
  const parts = displayStr.split("/");
  if (parts.length === 3) {
    const day = parts[0].padStart(2, "0");
    const month = parts[1].padStart(2, "0");
    const year = parts[2];
    return `${year}-${month}-${day}`;
  }
  return displayStr;
};

interface PresetTimeRange {
  label: string;
  getDateRange: () => [string, string]; // Returns [startDate, endDate] in YYYY-MM-DD format
}

type DateString = string; // YYYY-MM-DD format

interface CalendarDay {
  date: DateString;
  isCurrentMonth: boolean;
  isToday?: boolean;
  isSelected?: boolean;
  isInRange?: boolean;
  isRangeStart?: boolean;
  isRangeEnd?: boolean;
  isDisabled?: boolean;
}

type ViewMode = "days" | "months" | "years";

interface DatePickerProps {
  id?: string;
  value?: DateString | DateString[];
  onChange: (date: DateString | DateString[]) => void;
  mode?: "single" | "range";
  trigger?: React.ReactElement<{ onClick?: (e: React.MouseEvent) => void }>;
  className?: string;
  inputClassName?: string;
  placeholder?: string;
  yearRange?: number; // How many years to show in year picker (total)
  showPresets?: boolean; // Whether to show preset date ranges
  customPresets?: Array<{ label: string; onClick: () => void }>; // Custom preset buttons
  isOpen?: boolean; // Control open state from parent
  onOpenChange?: (isOpen: boolean) => void; // Callback when open state changes
  disableFutureDates?: boolean; // Whether to disable selection of future dates
  disablePastDates?: boolean; // Whether to disable selection of past dates
  minDate?: string; // Minimum selectable date (YYYY-MM-DD)
  disabled?: boolean; // Whether the input/control is disabled
  label?: string;
  showTimePicker?: boolean;
  /** Prefer opening above or below the trigger; "auto" picks based on viewport space. */
  dropdownPosition?: "top" | "bottom" | "auto";
  align?: "left" | "right"; // Alignment of the dropdown relative to the trigger
  allowManualInput?: boolean; // Whether to allow manual text input for date entry
  disableCalendar?: boolean; // Whether to disable the calendar popup entirely (manual input only)
  displayFormat?: "YYYY-MM-DD" | "DD/MM/YYYY"; // Display format for the date input
  /** When false, render the calendar in-place (e.g. inside SelectDropdown footers). Default true. */
  usePortal?: boolean;
  "data-testid"?: string;
}

export const DATE_PICKER_POPUP_ATTR = "data-date-picker-popup";
export const DATE_PICKER_TRIGGER_ATTR = "data-date-picker-trigger";

/** True when the event target is inside the portaled calendar or the trigger. */
export function isDatePickerInteractionTarget(
  target: EventTarget | null,
  popupRoot: HTMLElement | null,
  triggerRoot: HTMLElement | null,
): boolean {
  if (!(target instanceof Node)) return false;
  if (popupRoot?.contains(target)) return true;
  if (triggerRoot?.contains(target)) return true;
  if (target instanceof Element) {
    if (target.closest(`[${DATE_PICKER_POPUP_ATTR}]`)) return true;
    if (target.closest(`[${DATE_PICKER_TRIGGER_ATTR}]`)) return true;
  }
  return false;
}

/** Approximate calendar height for viewport flip calculations. */
export const DATE_PICKER_POPUP_ESTIMATED_HEIGHT = 420;
/** Approximate calendar width for horizontal viewport clamping. */
export const DATE_PICKER_POPUP_ESTIMATED_WIDTH = 320;

/** Allow focusable fields inside the popup (e.g. time input) while still preventing blur on the trigger. */
export function shouldAllowDatePickerPopupFocus(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return false;
  return Boolean(
    target.closest("input, textarea, select, [contenteditable='true']"),
  );
}
const DATE_PICKER_POPUP_GAP = 8;
const DATE_PICKER_VIEWPORT_PADDING = 8;

/** Resolve whether the calendar should open above or below the trigger. */
export function resolveDatePickerDropdownPosition(
  triggerEl: HTMLElement,
  preference: "top" | "bottom" | "auto" = "auto",
): "top" | "bottom" {
  if (preference !== "auto") {
    return preference;
  }

  const rect = triggerEl.getBoundingClientRect();
  const spaceBelow = window.innerHeight - rect.bottom - DATE_PICKER_POPUP_GAP;
  const spaceAbove = rect.top - DATE_PICKER_POPUP_GAP;

  if (spaceBelow >= DATE_PICKER_POPUP_ESTIMATED_HEIGHT) {
    return "bottom";
  }
  if (spaceAbove >= DATE_PICKER_POPUP_ESTIMATED_HEIGHT) {
    return "top";
  }
  return spaceAbove > spaceBelow ? "top" : "bottom";
}

function clampPopupLeft(left: number): number {
  const maxLeft =
    window.innerWidth -
    DATE_PICKER_POPUP_ESTIMATED_WIDTH -
    DATE_PICKER_VIEWPORT_PADDING;
  return Math.max(DATE_PICKER_VIEWPORT_PADDING, Math.min(left, maxLeft));
}

/** Available vertical space for the popup, in pixels. */
export function getPopupMaxHeight(
  triggerEl: HTMLElement,
  dropdownPosition: "top" | "bottom",
): number {
  const rect = triggerEl.getBoundingClientRect();
  const spaceBelow =
    window.innerHeight - rect.bottom - DATE_PICKER_POPUP_GAP - DATE_PICKER_VIEWPORT_PADDING;
  const spaceAbove = rect.top - DATE_PICKER_POPUP_GAP - DATE_PICKER_VIEWPORT_PADDING;
  const available = dropdownPosition === "bottom" ? spaceBelow : spaceAbove;
  return Math.max(180, Math.min(available, DATE_PICKER_POPUP_ESTIMATED_HEIGHT));
}

/** Popup anchor using viewport coordinates (works with portaled calendars). */
export function getPopupFixedStyle(
  triggerEl: HTMLElement,
  dropdownPosition: "top" | "bottom",
  align: "left" | "right",
): React.CSSProperties {
  const rect = triggerEl.getBoundingClientRect();
  const preferredLeft =
    align === "right"
      ? rect.right - DATE_PICKER_POPUP_ESTIMATED_WIDTH
      : rect.left;
  const left = clampPopupLeft(preferredLeft);

  if (dropdownPosition === "bottom") {
    return {
      position: "fixed",
      top: rect.bottom + DATE_PICKER_POPUP_GAP,
      left,
      zIndex: 10000,
    };
  }

  // Anchor from the bottom edge so we never rely on CSS transform (framer-motion
  // overwrites transform on the popup shell).
  return {
    position: "fixed",
    bottom: window.innerHeight - rect.top + DATE_PICKER_POPUP_GAP,
    left,
    zIndex: 10000,
  };
}

const Portal = ({ children }: { children: React.ReactNode }) => {
  if (typeof document === "undefined") return null;
  return ReactDOM.createPortal(children, document.body);
};

const DatePicker: React.FC<DatePickerProps> = ({
  id,
  value,
  onChange,
  mode = "single",
  trigger,
  className = "",
  inputClassName = "",
  placeholder = "DD/MM/YYYY",
  yearRange = 12,
  showPresets = true,
  customPresets,
  isOpen: controlledIsOpen,
  onOpenChange,
  disableFutureDates = false,
  disablePastDates = false,
  minDate,
  disabled = false,
  label,
  showTimePicker = false,
  dropdownPosition = "auto",
  align = "left",
  allowManualInput = true,
  disableCalendar = true,
  displayFormat = "DD/MM/YYYY",
  usePortal = true,
  "data-testid": dataTestId,
}) => {
  // Use controlled or uncontrolled open state
  const isControlled = controlledIsOpen !== undefined;
  const [uncontrolledIsOpen, setUncontrolledIsOpen] = useState(false);
  const isOpen = isControlled ? controlledIsOpen : uncontrolledIsOpen;

  const syncPopupLayout = useCallback(() => {
    if (!triggerRef.current) return;
    const resolved = resolveDatePickerDropdownPosition(
      triggerRef.current,
      dropdownPosition,
    );
    setResolvedDropdownPosition(resolved);
    setPopupStyle(getPopupFixedStyle(triggerRef.current, resolved, align));
    setPopupMaxHeight(getPopupMaxHeight(triggerRef.current, resolved));
  }, [dropdownPosition, align]);

  const setIsOpen = (open: boolean) => {
    if (open) {
      syncPopupLayout();
    }
    if (!isControlled) {
      setUncontrolledIsOpen(open);
    }
    if (onOpenChange && open !== isOpen) {
      onOpenChange(open);
    }
  };

  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [selectedDate, setSelectedDate] = useState<DateString | null>(
    mode === "single" && typeof value === "string" ? value : null
  );
  const [dateRange, setDateRange] = useState<[DateString?, DateString?]>(
    mode === "range" && Array.isArray(value) ? [value[0], value[1]] : []
  );
  const [viewMode, setViewMode] = useState<ViewMode>("days");
  const [yearViewBaseYear, setYearViewBaseYear] = useState(
    Math.floor(currentYear / yearRange) * yearRange
  );
  const [selectedTime, setSelectedTime] = useState("00:00");
  const [inputValue, setInputValue] = useState("");

  // Ref for click outside detection
  const datePickerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const [popupStyle, setPopupStyle] = useState<React.CSSProperties>({});
  const [popupMaxHeight, setPopupMaxHeight] = useState(
    DATE_PICKER_POPUP_ESTIMATED_HEIGHT,
  );
  const [resolvedDropdownPosition, setResolvedDropdownPosition] = useState<
    "top" | "bottom"
  >("bottom");

  // Anchor popup with fixed viewport coords (stable inside OS windows / portals)
  useEffect(() => {
    if (!isOpen || !triggerRef.current) return;

    syncPopupLayout();
    window.addEventListener("scroll", syncPopupLayout, true);
    window.addEventListener("resize", syncPopupLayout);
    return () => {
      window.removeEventListener("scroll", syncPopupLayout, true);
      window.removeEventListener("resize", syncPopupLayout);
    };
  }, [isOpen, syncPopupLayout]);

  // Close on outside mouseup (not mousedown) so month/year nav clicks complete first.
  useEffect(() => {
    if (!isOpen) return;

    const handleMouseUpOutside = (event: MouseEvent) => {
      if (
        isDatePickerInteractionTarget(
          event.target,
          datePickerRef.current,
          triggerRef.current,
        )
      ) {
        return;
      }
      setIsOpen(false);
    };

    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    };

    document.addEventListener("mouseup", handleMouseUpOutside, true);
    document.addEventListener("keydown", handleEscapeKey);
    return () => {
      document.removeEventListener("mouseup", handleMouseUpOutside, true);
      document.removeEventListener("keydown", handleEscapeKey);
    };
  }, [isOpen, setIsOpen]);

  // Update the month/year view when the DatePicker opens
  useEffect(() => {
    if (isOpen) {
      // Reset view mode to days when opened
      setViewMode("days");

      let dateToUse: Date | null = null;

      // Determine which date to use for setting the month/year view
      if (mode === "single" && selectedDate) {
        // If we have a selected date in single mode, use it
        dateToUse = new Date(selectedDate);
      } else if (mode === "range") {
        if (dateRange[0] && !dateRange[1]) {
          // If start date is selected but not end date, show month of start date
          dateToUse = new Date(dateRange[0]);
        } else if (dateRange[0] && dateRange[1]) {
          // If full range is selected, show month of start date
          dateToUse = new Date(dateRange[0]);
        }
      }

      // If no date is available from selection, use current date
      if (!dateToUse || isNaN(dateToUse.getTime())) {
        dateToUse = new Date();
      }

      // Set the month and year in the picker
      setCurrentMonth(dateToUse.getMonth());
      setCurrentYear(dateToUse.getFullYear());
    }
  }, [isOpen, mode, selectedDate, dateRange]);

  // Check if a date is in the future
  const isFutureDate = (dateStr: string): boolean => {
    if (!disableFutureDates) return false;
    const currentDate = new Date();
    currentDate.setHours(0, 0, 0, 0);
    const date = new Date(dateStr);
    return date > currentDate;
  };

  const isPastDate = (dateStr: string): boolean => {
    const currentDate = new Date();
    currentDate.setHours(0, 0, 0, 0);
    const date = new Date(dateStr);
    return date < currentDate;
  };

  const isDateDisabled = (dateStr: string): boolean => {
    if (disableFutureDates && isFutureDate(dateStr)) return true;
    if (minDate && dateStr < minDate) return true;
    if (disablePastDates && isPastDate(dateStr)) return true;
    return false;
  };

  // Define preset time ranges
  const presetTimeRanges: PresetTimeRange[] = [
    {
      label: "Today",
      getDateRange: () => {
        const today = new Date();
        return [formatDate(today), formatDate(today)] as [string, string];
      },
    },
    {
      label: "Yesterday",
      getDateRange: () => {
        const today = new Date();
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        return [formatDate(yesterday), formatDate(yesterday)] as [
          string,
          string,
        ];
      },
    },
    {
      label: "Last 7 days",
      getDateRange: () => {
        const today = new Date();
        const lastWeek = new Date(today);
        lastWeek.setDate(lastWeek.getDate() - 6); // 6 days ago + today = 7 days
        return [formatDate(lastWeek), formatDate(today)] as [string, string];
      },
    },
    {
      label: "Last 30 days",
      getDateRange: () => {
        const today = new Date();
        const lastMonth = new Date(today);
        lastMonth.setDate(lastMonth.getDate() - 29); // 29 days ago + today = 30 days
        return [formatDate(lastMonth), formatDate(today)] as [string, string];
      },
    },
    {
      label: "This month",
      getDateRange: () => {
        const today = new Date();
        const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
        return [formatDate(firstDay), formatDate(today)] as [string, string];
      },
    },
    {
      label: "Last month",
      getDateRange: () => {
        const today = new Date();
        const firstDayLastMonth = new Date(
          today.getFullYear(),
          today.getMonth() - 1,
          1
        );
        const lastDayLastMonth = new Date(
          today.getFullYear(),
          today.getMonth(),
          0
        );
        return [
          formatDate(firstDayLastMonth),
          formatDate(lastDayLastMonth),
        ] as [string, string];
      },
    },
  ];

  const pickingEndDate = isPickingEndDate(mode, dateRange);

  const presetsToShow =
    pickingEndDate && !customPresets
      ? presetTimeRanges.filter((preset) => isEndDatePreset(preset.label))
      : presetTimeRanges;

  // Handle preset selection
  const handlePresetSelect = (preset: PresetTimeRange) => {
    const newRange = preset.getDateRange();

    if (mode === "range") {
      const rangeToApply =
        pickingEndDate && dateRange[0]
          ? completeRangeWithEnd(dateRange[0], newRange[1])
          : newRange;
      setDateRange([rangeToApply[0], rangeToApply[1]]);
      onChange(rangeToApply);
      setIsOpen(false);
    } else {
      setSelectedDate(newRange[0]);
      onChange(newRange[0]);
      setIsOpen(false);
    }
  };

  // Get today's date as YYYY-MM-DD
  const today = new Date().toISOString().split("T")[0];

  // Format date for display
  const formatDisplayDate = (dateStr?: DateString) => {
    if (!dateStr) return "";
    return formatDateForDisplay(dateStr, displayFormat);
  };

  // Get display value based on mode
  const getDisplayValue = () => {
    if (mode === "single") {
      if (!selectedDate) {
        return placeholder;
      }
      return showTimePicker
        ? `${selectedDate} ${selectedTime}`
        : formatDisplayDate(selectedDate);
    } else {
      if (dateRange[0] && dateRange[1]) {
        return `${formatDisplayDate(dateRange[0])} - ${formatDisplayDate(
          dateRange[1]
        )}`;
      } else if (dateRange[0]) {
        return `${formatDisplayDate(dateRange[0])} - Select end date`;
      }
      return placeholder;
    }
  };

  const handleTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedTime(e.target.value);
    // If a date is already selected, update it with the new time
    if (selectedDate) {
      onChange(`${selectedDate} ${e.target.value}`);
    }
  };

  const handleManualInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);

    if (!newValue.trim()) {
      setSelectedDate(null);
      onChange(showTimePicker ? ` ${selectedTime}` : "");
      return;
    }

    // Parse the date based on the display format
    let apiDate: string | null = null;

    if (displayFormat === "DD/MM/YYYY") {
      // Try to parse DD/MM/YYYY format
      const ddmmyyyyRegex = /^\d{2}\/\d{2}\/\d{4}$/;
      if (ddmmyyyyRegex.test(newValue)) {
        apiDate = parseDisplayDate(newValue, displayFormat);
      } else if (/^\d{4}-\d{2}-\d{2}$/.test(newValue)) {
        apiDate = newValue;
      }
    } else {
      // Try to parse YYYY-MM-DD format
      const yyyymmddRegex = /^\d{4}-\d{2}-\d{2}$/;
      if (yyyymmddRegex.test(newValue)) {
        apiDate = newValue;
      } else if (/^\d{2}\/\d{2}\/\d{4}$/.test(newValue)) {
        apiDate = parseDisplayDate(newValue, "DD/MM/YYYY");
      }
    }

    // Validate and update if we have a valid API date
    if (apiDate) {
      const dateObj = new Date(apiDate);
      if (!isNaN(dateObj.getTime())) {
        // Valid date - update the selectedDate and call onChange
        setSelectedDate(apiDate);
        onChange(showTimePicker ? `${apiDate} ${selectedTime}` : apiDate);

        // Update the month/year view
        setCurrentMonth(dateObj.getMonth());
        setCurrentYear(dateObj.getFullYear());
      }
    }
  };

  // Effect to update internal state when props change
  useEffect(() => {
    if (mode === "single") {
      if (typeof value === "string") {
        const [datePart, timePart] = value.split(" ");
        setSelectedDate(datePart || null);
        // Format the date for display based on displayFormat
        setInputValue(formatDateForDisplay(datePart || "", displayFormat));

        // Set current month/year based on selected date
        if (datePart) {
          const dateObj = new Date(datePart);
          if (!isNaN(dateObj.getTime())) {
            setCurrentMonth(dateObj.getMonth());
            setCurrentYear(dateObj.getFullYear());
          }
        }
        if (showTimePicker) {
          setSelectedTime(timePart || "00:00");
        }
      } else {
        setSelectedDate(null);
        setInputValue("");
        if (showTimePicker) {
          setSelectedTime("00:00");
        }
      }
    } else if (mode === "range" && Array.isArray(value)) {
      setDateRange([value[0], value[1]]);

      // Set current month/year based on start date of the range
      if (value[0]) {
        const dateObj = new Date(value[0]);
        if (!isNaN(dateObj.getTime())) {
          setCurrentMonth(dateObj.getMonth());
          setCurrentYear(dateObj.getFullYear());
        }
      }
    }
  }, [value, mode, showTimePicker, displayFormat]);

  // Generate calendar days for current month view
  const generateCalendarDays = (): CalendarDay[] => {
    const days: CalendarDay[] = [];

    // Get days from previous month to fill first week
    const firstDay = getFirstDayOfMonth(currentYear, currentMonth);
    const prevMonthDays = getDaysInMonth(
      currentMonth === 0 ? currentYear - 1 : currentYear,
      currentMonth === 0 ? 11 : currentMonth - 1
    );

    for (let i = 0; i < firstDay; i++) {
      const day = prevMonthDays - firstDay + i + 1;
      const year = currentMonth === 0 ? currentYear - 1 : currentYear;
      const month = currentMonth === 0 ? 11 : currentMonth - 1;
      const date = `${year}-${String(month + 1).padStart(2, "0")}-${String(
        day
      ).padStart(2, "0")}`;

      let isInRange = false;
      let isRangeStart = false;
      let isRangeEnd = false;

      if (mode === "range" && dateRange[0] && dateRange[1]) {
        // Only set isInRange if start and end dates are different
        isInRange =
          date >= dateRange[0] &&
          date <= dateRange[1] &&
          dateRange[0] !== dateRange[1];
        isRangeStart = date === dateRange[0];
        isRangeEnd = date === dateRange[1];
      }

      days.push({
        date,
        isCurrentMonth: false,
        isDisabled: isDateDisabled(date),
        isInRange,
        isRangeStart,
        isRangeEnd,
      });
    }

    // Current month days
    const daysInMonth = getDaysInMonth(currentYear, currentMonth);
    for (let day = 1; day <= daysInMonth; day++) {
      const date = `${currentYear}-${String(currentMonth + 1).padStart(
        2,
        "0"
      )}-${String(day).padStart(2, "0")}`;

      let isInRange = false;
      let isRangeStart = false;
      let isRangeEnd = false;

      if (mode === "range" && dateRange[0] && dateRange[1]) {
        // Only set isInRange if start and end dates are different
        isInRange =
          date >= dateRange[0] &&
          date <= dateRange[1] &&
          dateRange[0] !== dateRange[1];
        isRangeStart = date === dateRange[0];
        isRangeEnd = date === dateRange[1];
      }

      days.push({
        date,
        isCurrentMonth: true,
        isToday: date === today,
        isSelected:
          mode === "single"
            ? date === selectedDate
            : date === dateRange[0] || date === dateRange[1],
        isInRange,
        isRangeStart,
        isRangeEnd,
        isDisabled: isDateDisabled(date),
      });
    }

    // Next month days to complete the calendar grid (6 rows x 7 days = 42 cells)
    const totalDaysNeeded = 42;
    const remainingDays = totalDaysNeeded - days.length;
    for (let day = 1; day <= remainingDays; day++) {
      const year = currentMonth === 11 ? currentYear + 1 : currentYear;
      const month = currentMonth === 11 ? 0 : currentMonth + 1;
      const date = `${year}-${String(month + 1).padStart(2, "0")}-${String(
        day
      ).padStart(2, "0")}`;

      let isInRange = false;
      let isRangeStart = false;
      let isRangeEnd = false;

      if (mode === "range" && dateRange[0] && dateRange[1]) {
        // Only set isInRange if start and end dates are different
        isInRange =
          date >= dateRange[0] &&
          date <= dateRange[1] &&
          dateRange[0] !== dateRange[1];
        isRangeStart = date === dateRange[0];
        isRangeEnd = date === dateRange[1];
      }

      days.push({
        date,
        isCurrentMonth: false,
        isDisabled: isDateDisabled(date),
        isInRange,
        isRangeStart,
        isRangeEnd,
      });
    }

    return days;
  };

  // Generate months for month view
  const generateMonths = () => {
    const monthNames = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];

    return monthNames.map((name, index) => {
      const currentDate = new Date();
      const isCurrentMonth =
        currentDate.getMonth() === index &&
        currentDate.getFullYear() === currentYear;

      return {
        name,
        index,
        isCurrentMonth,
      };
    });
  };

  // Generate years for year view
  const generateYears = () => {
    const years = [];
    const startYear = yearViewBaseYear;
    const currentDate = new Date();

    for (let i = 0; i < yearRange; i++) {
      const year = startYear + i;
      years.push({
        year,
        isCurrentYear: currentDate.getFullYear() === year,
      });
    }
    return years;
  };

  // Handle date selection
  const handleSelectDate = (day: CalendarDay) => {
    // Prevent selecting disabled dates
    if (day.isDisabled) return;

    if (mode === "single") {
      const finalDate = showTimePicker
        ? `${day.date} ${selectedTime}`
        : day.date;
      setSelectedDate(day.date);
      onChange(finalDate);
      if (!showTimePicker) {
        setIsOpen(false);
      }
    } else {
      // Range selection logic
      if (!dateRange[0] || (dateRange[0] && dateRange[1])) {
        // Start new range
        setDateRange([day.date, undefined]);
      } else {
        // Complete the range
        let newRange: [DateString, DateString];
        if (day.date < dateRange[0]) {
          // If selecting a date before the start date, swap them
          newRange = [day.date, dateRange[0]];
        } else {
          newRange = [dateRange[0], day.date];
        }
        setDateRange(newRange);
        onChange(newRange); // Call onChange with the complete range
        setIsOpen(false); // Close the picker when range is complete
      }
    }
  };

  // Handle month selection
  const handleSelectMonth = (monthIndex: number) => {
    setCurrentMonth(monthIndex);
    setViewMode("days");
  };

  // Handle year selection
  const handleSelectYear = (year: number) => {
    setCurrentYear(year);
    setViewMode("months");
  };

  // Navigation methods
  const goToPreviousMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
  };

  const goToNextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
  };

  const goToPreviousYear = () => {
    setCurrentYear(currentYear - 1);
  };

  const goToNextYear = () => {
    setCurrentYear(currentYear + 1);
  };

  const goToPreviousYearRange = () => {
    setYearViewBaseYear(yearViewBaseYear - yearRange);
  };

  const goToNextYearRange = () => {
    setYearViewBaseYear(yearViewBaseYear + yearRange);
  };

  const calendarDays = generateCalendarDays();
  const months = generateMonths();
  const years = generateYears();
  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  return (
    <div className={`flex flex-col w-full ${className}`}>
      {label && (
        <label
          htmlFor={id}
          className={clsx(
            "text-xs uppercase tracking-widest flex items-center font-mono",
            disabled
              ? "text-slate-400 dark:text-slate-500"
              : "text-slate-800 dark:text-slate-300",
          )}
        >
          <span className="px-2">{label}</span>
        </label>
      )}
      <div
        className={clsx(label && "mt-2", "relative")}
        ref={triggerRef}
        {...{ [DATE_PICKER_TRIGGER_ATTR]: "" }}
      >
        {trigger ? (
          React.cloneElement(trigger, {
            onClick: (e: React.MouseEvent) => {
              if (disabled) return;
              e.stopPropagation();
              trigger.props.onClick?.(e);
              setIsOpen(!isOpen);
            },
          })
        ) : allowManualInput && mode === "single" ? (
          <div className="relative w-full">
            <input
              id={id}
              type="text"
              value={inputValue}
              disabled={disabled}
              onChange={handleManualInput}
              onClick={() => {
                if (!disabled && !disableCalendar && !isOpen) {
                  setIsOpen(true);
                }
              }}
              placeholder={placeholder}
              data-testid={dataTestId}
              className={clsx(
                "block w-full rounded-xl px-3 py-1.5 text-base h-10",
                "outline-none border",
                disabled
                  ? [
                      "cursor-not-allowed border-slate-200 bg-slate-100 text-slate-400",
                      "placeholder:text-slate-400",
                      "dark:border-slate-700 dark:bg-slate-800/60 dark:text-slate-500",
                      "dark:placeholder:text-slate-600",
                    ]
                  : [
                      "bg-white text-slate-900 dark:bg-slate-900/80 dark:text-slate-200",
                      "border-slate-200 dark:border-slate-600",
                      "placeholder:text-slate-400 dark:placeholder:text-slate-500",
                      "focus:ring-2 focus:ring-indigo-400 focus:ring-offset-2 focus:ring-offset-white dark:focus:ring-offset-slate-700 dark:focus:ring-indigo-400",
                    ],
                !disableCalendar && "pr-10",
                inputClassName
              )}
            />
            {!disableCalendar && (
              <button
                type="button"
                disabled={disabled}
                onClick={(e) => {
                  e.stopPropagation();
                  if (!disabled) setIsOpen(!isOpen);
                }}
                className={clsx(
                  "absolute inset-y-0 right-0 flex items-center pr-3",
                  disabled
                    ? "cursor-not-allowed text-slate-300 dark:text-slate-600"
                    : "text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
                )}
                tabIndex={-1}
              >
                <FontAwesomeIcon icon={faCalendar} className="h-4 w-4" />
              </button>
            )}
          </div>
        ) : (
          <button
            type="button"
            id={id}
            disabled={disabled}
            data-testid={dataTestId}
            onClick={() => {
              if (!disabled) setIsOpen(!isOpen);
            }}
            className={clsx(
              "block w-full rounded-xl px-3 py-1.5 text-base h-10 text-left relative",
              "outline-none border",
              disabled
                ? [
                    "cursor-not-allowed border-slate-200 bg-slate-100 text-slate-400",
                    "dark:border-slate-700 dark:bg-slate-800/60 dark:text-slate-500",
                  ]
                : [
                    "bg-white text-slate-900 dark:bg-slate-900/80 dark:text-slate-200",
                    "border-slate-200 dark:border-slate-600",
                    "focus:ring-2 focus:ring-indigo-400 focus:ring-offset-2 focus:ring-offset-white dark:focus:ring-offset-slate-700 dark:focus:ring-indigo-400",
                  ],
              !disableCalendar && "pr-10",
              inputClassName
            )}
          >
            <span className={clsx(!getDisplayValue() && "text-slate-400 dark:text-slate-500")}>
              {getDisplayValue() || placeholder}
            </span>
            {!disableCalendar && (
              <span
                className={clsx(
                  "absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none",
                  disabled ? "text-slate-300 dark:text-slate-600" : "text-slate-400"
                )}
              >
                <FontAwesomeIcon icon={faCalendar} className="h-4 w-4" />
              </span>
            )}
          </button>
        )}
        <AnimatePresence>
          {isOpen && !disableCalendar && (() => {
            const popupShellClass = clsx(
              "w-auto min-w-[320px] rounded-2xl bg-white p-4 shadow-lg dark:bg-gray-800 dark:ring-1 dark:ring-white/10 border border-gray-200 dark:border-gray-700",
              usePortal ? "overflow-y-auto" : "",
            );
            const popupShellStyle: React.CSSProperties = usePortal
              ? { ...popupStyle, maxHeight: popupMaxHeight }
              : popupStyle;
            const popupBody = (
              <div className="w-full">
                    {/* Preset ranges section (only for range mode or if specifically enabled) */}
                    {showPresets && (
                      <div className="mb-4 border-b pb-4 border-gray-200 dark:border-gray-700">
                        <div className="text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                          {pickingEndDate ? "End Date" : "Preset Ranges"}
                        </div>
                        {pickingEndDate && dateRange[0] && (
                          <p className="mb-2 text-xs text-gray-500 dark:text-gray-400">
                            Start:{" "}
                            {formatDateForDisplay(dateRange[0], displayFormat)} —
                            select end date
                          </p>
                        )}
                        <div className="flex flex-wrap gap-2">
                          {customPresets
                            ? customPresets.map((preset) => (
                                <button
                                  key={preset.label}
                                  type="button"
                                  onClick={preset.onClick}
                                  className="px-3 py-1 text-xs font-medium rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-200 dark:border-gray-700 dark:hover:bg-gray-700"
                                >
                                  {preset.label}
                                </button>
                              ))
                            : presetsToShow.map((preset) => (
                                <button
                                  key={preset.label}
                                  type="button"
                                  onClick={() => handlePresetSelect(preset)}
                                  className="px-3 py-1 text-xs font-medium rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-200 dark:border-gray-700 dark:hover:bg-gray-700"
                                >
                                  {pickingEndDate
                                    ? getEndDatePresetLabel(preset.label)
                                    : preset.label}
                                </button>
                              ))}
                        </div>
                      </div>
                    )}

                    {/* Month/Year navigation header */}
                    <div className="flex items-center justify-between text-gray-900 dark:text-white">
                      {viewMode === "days" && (
                        <>
                          <button
                            type="button"
                            className="-m-1.5 flex flex-none items-center justify-center p-1.5 text-gray-400 hover:text-gray-500 dark:text-gray-500 dark:hover:text-gray-400"
                            onClick={goToPreviousMonth}
                          >
                            <span className="sr-only">Previous month</span>
                            <FontAwesomeIcon
                              icon={faChevronLeft}
                              className="w-5 h-5"
                            />
                          </button>
                          <button
                            type="button"
                            onClick={() => setViewMode("months")}
                            className="flex-auto text-base font-semibold hover:bg-gray-100 dark:hover:bg-gray-700 px-2 py-1 rounded"
                          >
                            {monthNames[currentMonth]} {currentYear}
                          </button>
                          <button
                            type="button"
                            className="-m-1.5 flex flex-none items-center justify-center p-1.5 text-gray-400 hover:text-gray-500 dark:text-gray-500 dark:hover:text-gray-400"
                            onClick={goToNextMonth}
                          >
                            <span className="sr-only">Next month</span>
                            <FontAwesomeIcon
                              icon={faChevronRight}
                              className="w-5 h-5"
                            />
                          </button>
                        </>
                      )}

                      {viewMode === "months" && (
                        <>
                          <button
                            type="button"
                            className="-m-1.5 flex flex-none items-center justify-center p-1.5 text-gray-400 hover:text-gray-500 dark:text-gray-500 dark:hover:text-gray-400"
                            onClick={goToPreviousYear}
                          >
                            <span className="sr-only">Previous year</span>
                            <FontAwesomeIcon
                              icon={faChevronLeft}
                              className="w-5 h-5"
                            />
                          </button>
                          <button
                            type="button"
                            onClick={() => setViewMode("years")}
                            className="flex-auto text-base font-semibold hover:bg-gray-100 dark:hover:bg-gray-700 px-2 py-1 rounded"
                          >
                            {currentYear}
                          </button>
                          <button
                            type="button"
                            className="-m-1.5 flex flex-none items-center justify-center p-1.5 text-gray-400 hover:text-gray-500 dark:text-gray-500 dark:hover:text-gray-400"
                            onClick={goToNextYear}
                          >
                            <span className="sr-only">Next year</span>
                            <FontAwesomeIcon
                              icon={faChevronRight}
                              className="w-5 h-5"
                            />
                          </button>
                        </>
                      )}

                      {viewMode === "years" && (
                        <>
                          <button
                            type="button"
                            className="-m-1.5 flex flex-none items-center justify-center p-1.5 text-gray-400 hover:text-gray-500 dark:text-gray-500 dark:hover:text-gray-400"
                            onClick={goToPreviousYearRange}
                          >
                            <span className="sr-only">Previous years</span>
                            <FontAwesomeIcon
                              icon={faAnglesLeft}
                              className="w-5 h-5"
                            />
                          </button>
                          <div className="flex-auto text-base font-semibold text-center">
                            {yearViewBaseYear} -{" "}
                            {yearViewBaseYear + yearRange - 1}
                          </div>
                          <button
                            type="button"
                            className="-m-1.5 flex flex-none items-center justify-center p-1.5 text-gray-400 hover:text-gray-500 dark:text-gray-500 dark:hover:text-gray-400"
                            onClick={goToNextYearRange}
                          >
                            <span className="sr-only">Next years</span>
                            <FontAwesomeIcon
                              icon={faAnglesRight}
                              className="w-5 h-5"
                            />
                          </button>
                        </>
                      )}
                    </div>

                    {/* Days View */}
                    {viewMode === "days" && (
                      <>
                        {/* Weekday headers */}
                        <div className="mt-6 grid grid-cols-7 text-xs text-gray-500 dark:text-gray-400">
                          {daysOfWeek.map((day) => (
                            <div key={day} className="text-center">
                              {day}
                            </div>
                          ))}
                        </div>

                        {/* Calendar grid */}
                        <div className="mt-2 grid grid-cols-7 gap-px rounded-lg bg-gray-200 dark:bg-gray-700 text-sm ring-1 ring-gray-200 dark:ring-gray-700 shadow-sm">
                          {calendarDays.map((day, dayIdx) => {
                            return (
                              <button
                                key={day.date}
                                type="button"
                                onClick={() => handleSelectDate(day)}
                                className={classNames(
                                  "relative py-1.5 focus:z-10 focus:outline-none",
                                  day.isCurrentMonth
                                    ? "bg-white dark:bg-gray-800"
                                    : "bg-gray-50 dark:bg-gray-900 text-gray-400 dark:text-gray-500",
                                  day.isSelected && "font-semibold",
                                  day.isToday &&
                                    !day.isSelected &&
                                    "text-indigo-600 dark:text-indigo-400",
                                  day.isDisabled
                                    ? "text-gray-300 dark:text-gray-600 cursor-not-allowed"
                                    : "hover:bg-gray-100 dark:hover:bg-gray-600",
                                  dayIdx === 0 && "rounded-tl-lg",
                                  dayIdx === 6 && "rounded-tr-lg",
                                  dayIdx === calendarDays.length - 7 &&
                                    "rounded-bl-lg",
                                  dayIdx === calendarDays.length - 1 &&
                                    "rounded-br-lg"
                                )}
                                disabled={day.isDisabled}
                              >
                                {/* Range background - positioned behind the date number */}
                                {day.isInRange && (
                                  <div
                                    className={classNames(
                                      "absolute inset-0",
                                      day.isRangeStart
                                        ? "bg-indigo-50 dark:bg-indigo-800/30 rounded-l-full border-l border-t border-b border-indigo-200 dark:border-indigo-700"
                                        : day.isRangeEnd
                                          ? "bg-indigo-50 dark:bg-indigo-800/30 rounded-r-full border-r border-t border-b border-indigo-200 dark:border-indigo-700"
                                          : "bg-indigo-50 dark:bg-indigo-800/30 border-t border-b border-indigo-200 dark:border-indigo-700"
                                    )}
                                    style={{ zIndex: 0 }}
                                  />
                                )}

                                <time
                                  dateTime={day.date}
                                  className={classNames(
                                    "mx-auto flex h-7 w-7 items-center justify-center rounded-full relative z-[1]",
                                    day.isSelected &&
                                      day.isToday &&
                                      "bg-indigo-600 text-white dark:bg-indigo-500",
                                    day.isSelected &&
                                      !day.isToday &&
                                      "bg-gray-900 text-white dark:bg-white dark:text-gray-900",
                                    day.isRangeStart &&
                                      "bg-indigo-600 text-white dark:bg-indigo-500",
                                    day.isRangeEnd &&
                                      "bg-indigo-600 text-white dark:bg-indigo-500",
                                    !day.isSelected &&
                                      !day.isRangeStart &&
                                      !day.isRangeEnd &&
                                      day.isToday &&
                                      "text-indigo-500 dark:text-indigo-400",
                                    !day.isSelected &&
                                      !day.isRangeStart &&
                                      !day.isRangeEnd &&
                                      !day.isToday &&
                                      "text-gray-900 dark:text-white"
                                  )}
                                >
                                  {day.date.split("-").pop()?.replace(/^0/, "")}
                                </time>
                              </button>
                            );
                          })}
                        </div>
                      </>
                    )}

                    {/* Months View */}
                    {viewMode === "months" && (
                      <div className="mt-6 grid grid-cols-3 gap-4">
                        {months.map((month) => (
                          <button
                            key={month.index}
                            type="button"
                            onClick={() => handleSelectMonth(month.index)}
                            className={classNames(
                              "h-12 rounded-lg text-sm hover:bg-gray-100 dark:hover:bg-gray-700",
                              month.isCurrentMonth
                                ? "bg-indigo-50 text-indigo-600 font-semibold dark:bg-indigo-800/30 dark:text-indigo-400"
                                : "bg-white text-gray-700 dark:bg-gray-800 dark:text-gray-300"
                            )}
                          >
                            {month.name}
                          </button>
                        ))}
                      </div>
                    )}

                    {/* Years View */}
                    {viewMode === "years" && (
                      <div className="mt-6 grid grid-cols-2 gap-4">
                        {years.map((yearObj) => (
                          <button
                            key={yearObj.year}
                            type="button"
                            onClick={() => handleSelectYear(yearObj.year)}
                            className={classNames(
                              "h-12 rounded-lg text-sm hover:bg-gray-100 dark:hover:bg-gray-700",
                              yearObj.isCurrentYear
                                ? "bg-indigo-50 text-indigo-600 font-semibold dark:bg-indigo-800/30 dark:text-indigo-400"
                                : "bg-white text-gray-700 dark:bg-gray-800 dark:text-gray-300"
                            )}
                          >
                            {yearObj.year}
                          </button>
                        ))}
                      </div>
                    )}
                  {showTimePicker && (
                    <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                      <FormInput
                        type="time"
                        value={selectedTime}
                        onChange={handleTimeChange}
                        icon={
                          <FontAwesomeIcon
                            icon={faClock}
                            className="text-slate-400 dark:text-slate-500"
                          />
                        }
                      />
                    </div>
                  )}
              </div>
            );

            const calendarPopup = usePortal ? (
              <div
                ref={datePickerRef}
                {...{ [DATE_PICKER_POPUP_ATTR]: "" }}
                style={popupShellStyle}
                className={popupShellClass}
                onMouseDown={(e) => {
                  if (shouldAllowDatePickerPopupFocus(e.target)) return;
                  e.preventDefault();
                }}
              >
                {popupBody}
              </div>
            ) : (
              <motion.div
                ref={datePickerRef}
                {...{ [DATE_PICKER_POPUP_ATTR]: "" }}
                initial={{
                  opacity: 0,
                  y: resolvedDropdownPosition === "bottom" ? -10 : 10,
                }}
                animate={{ opacity: 1, y: 0 }}
                exit={{
                  opacity: 0,
                  y: resolvedDropdownPosition === "bottom" ? -10 : 10,
                }}
                transition={{ duration: 0.15 }}
                style={popupShellStyle}
                className={popupShellClass}
                onMouseDown={(e) => {
                  if (shouldAllowDatePickerPopupFocus(e.target)) return;
                  e.preventDefault();
                }}
              >
                {popupBody}
              </motion.div>
            );

            return usePortal ? <Portal>{calendarPopup}</Portal> : calendarPopup;
          })()}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default DatePicker;
